import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import {
  Observable,
  of as observableOf,
  throwError as observableThrowError,
} from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { CurrentUserService } from '®shared/current-user';
import {
  DEFAULT_NAVIGATION_EXTRAS,
  handleRenavigationError,
  idWithoutDotHtml,
} from '®shared/router';

import { ItemResolverStoreService } from '../resolver-store';

@Injectable()
export class CanActivateItemView implements CanActivate {
  constructor(
    private currentUserService: CurrentUserService,
    private itemResolverStoreService: ItemResolverStoreService,
    private router: Router,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const itemId = idWithoutDotHtml(route.params.itemId);
    const { version, tenantCode } = route.params;

    return this.currentUserService.getCurrentUser().pipe(
      mergeMap(currentUser => {
        const { oauthUser } = currentUser;
        const { isAnonymous } = oauthUser;

        // If we find a published item, return true to allow access
        // If no published item is found BUT the user is non anonymous, redirect to draft;
        // otherwise, redirect to a 404 error page
        return this.itemResolverStoreService.hasPublishedItem(itemId).pipe(
          catchError(error => {
            if (!(error instanceof HttpErrorResponse)) {
              // unknown errors, rethrown
              return observableThrowError(error);
            }

            if (error.status === 404 && !isAnonymous) {
              // a 404 means there's no published item, so check for a draft
              this.router.navigate(
                [version, tenantCode, 'items', itemId, 'modes', 'view_draft'],
                DEFAULT_NAVIGATION_EXTRAS,
              );
              return observableOf(false);
            } else if (error.status === 403 && isAnonymous) {
              // a 403 means the list is private, so force login
              this.currentUserService.login();
              return observableOf(false);
            }

            // all other errors are re-thrown
            return observableThrowError(error);
          }),
        );
      }),
      catchError(handleRenavigationError(this.router)),
    );
  }
}
